import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { generateLinkProps } from '../../util/routes';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  propTypes,
} from '../../util/types';

import {
  Modal,
  NamedLink,
  ExternalLink,
  Heading,
  StripeConnectAccountForm,
  StripeConnectAccountStatusBox,
} from '../../components';
import EditIcon from './EditIcon';
import PromoteIcon from './PromoteIcon';

import css from './ListingPage.module.css';

const STRIPE_ONBOARDING_RETURN_URL_SUCCESS = 'success';
const STRIPE_ONBOARDING_RETURN_URL_FAILURE = 'failure';
const STRIPE_SETUP_PROMPT_SESSION_KEY = 'stripeSetupPromptListingId';

const createReturnURL = (returnURLType, rootURL, routes) => {
  const path = createResourceLocatorString(
    'StripePayoutOnboardingPage',
    routes,
    { returnURLType },
    {}
  );
  const root = rootURL.replace(/\/$/, '');
  return `${root}${path}`;
};

const getStripeAccountData = stripeAccount => stripeAccount?.attributes?.stripeAccountData || null;

const getBankAccountLast4Digits = stripeAccountData =>
  stripeAccountData && stripeAccountData.external_accounts.data.length > 0
    ? stripeAccountData.external_accounts.data[0].last4
    : null;

const hasRequirements = (stripeAccountData, requirementType) =>
  stripeAccountData != null &&
  stripeAccountData.requirements &&
  Array.isArray(stripeAccountData.requirements[requirementType]) &&
  stripeAccountData.requirements[requirementType].length > 0;

const handleGetStripeConnectAccountLinkFn = (getLinkFn, commonParams) => type => () => {
  getLinkFn({ type, ...commonParams })
    .then(url => {
      window.location.href = url;
    })
    .catch(err => console.error(err));
};

const CTAButtonMaybe = props => {
  const { data, routeConfiguration, userId, userEmail, listingId, isPendingApproval } = props;

  const hasValidType = data?.type && data.type !== 'none';
  const isCTAEnabled = hasValidType && isPendingApproval;

  if (!isCTAEnabled) {
    return null;
  }

  const { type, text, href } = data;
  const ctaLink = generateLinkProps(type, href, routeConfiguration, userId, userEmail, listingId);
  const isInternalLink = type === 'internal' && ctaLink.route;

  return isInternalLink ? (
    <NamedLink
      name={ctaLink.route.name}
      to={ctaLink.route.to}
      params={ctaLink.route.params}
      className={css.actionBarCTA}
    >
      {text}
    </NamedLink>
  ) : (
    <ExternalLink href={ctaLink.link} className={css.actionBarCTA}>
      {text}
    </ExternalLink>
  );
};

const ConfirmModal = ({ type, onConfirm, onCancel }) => (
  <div className={css.modalOverlay} onClick={onCancel}>
    <div className={css.modalBox} onClick={e => e.stopPropagation()}>
      <p className={css.modalTitle}>
        {type === 'hide' ? (
          <FormattedMessage id="ListingPage.hideListingConfirmQuestion" />
        ) : (
          <FormattedMessage id="ListingPage.deleteListingConfirmQuestion" />
        )}
      </p>
      <p className={css.modalMessage}>
        {type === 'hide' ? (
          <FormattedMessage id="ListingPage.hideListingConfirmMessage" />
        ) : (
          <FormattedMessage id="ListingPage.deleteListingConfirmMessage" />
        )}
      </p>
      <div className={css.modalActions}>
        <button className={css.modalCancel} onClick={onCancel}>
          <FormattedMessage id="ListingPage.deleteListingCancel" />
        </button>
        <button className={css.modalConfirm} onClick={onConfirm}>
          {type === 'hide' ? (
            <FormattedMessage id="ListingPage.hideListingConfirm" />
          ) : (
            <FormattedMessage id="ListingPage.deleteListingConfirm" />
          )}
        </button>
      </div>
    </div>
  </div>
);

export const ActionBarMaybe = props => {
  const {
    rootClassName,
    className,
    isOwnListing,
    listing,
    currentUser,
    editParams,
    showNoPayoutDetailsSet,
    onCloseListing,
    onDeleteListing,
    stripeAccount,
    stripeAccountFetched,
    payoutDetailsSaveInProgress,
    payoutDetailsSaved,
    getAccountLinkInProgress,
    getAccountLinkError,
    createStripeAccountError,
    updateStripeAccountError,
    fetchStripeAccountError,
    authScopes,
    onPayoutDetailsChange,
    onPayoutDetailsSubmit,
    onGetStripeConnectAccountLink,
    onManageDisableScrolling,
    onFetchStripeAccount,
  } = props;

 const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [showStripeSetupPrompt, setShowStripeSetupPrompt] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();

  const classes = classNames(rootClassName || css.actionBar, className);

  const state = listing.attributes.state;
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isDraft = state === LISTING_STATE_DRAFT;

  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const approvalToPublishOptions =
    config?.accessControl?.listings?.requireApprovalToPublishOptions?.callToAction || {};
  const currentUserLoaded = !!currentUser?.id;
  const stripeConnected = currentUserLoaded && !!stripeAccount && !!stripeAccount.id;
  const stripeAccountData = stripeConnected ? getStripeAccountData(stripeAccount) : null;
  const requirementsMissing =
    stripeAccount &&
    (hasRequirements(stripeAccountData, 'past_due') ||
      hasRequirements(stripeAccountData, 'currently_due'));
  const savedCountry = stripeAccountData ? stripeAccountData.country : null;
  const savedAccountType = stripeAccountData ? stripeAccountData.business_type : null;
  const rootURL = config.marketplaceRootURL;
  const successURL = createReturnURL(STRIPE_ONBOARDING_RETURN_URL_SUCCESS, rootURL, routeConfiguration);
  const failureURL = createReturnURL(STRIPE_ONBOARDING_RETURN_URL_FAILURE, rootURL, routeConfiguration);
  const accountId = stripeConnected ? stripeAccount.id : null;
  const handleGetStripeConnectAccountLink = handleGetStripeConnectAccountLinkFn(
    onGetStripeConnectAccountLink,
    { accountId, successURL, failureURL }
  );
  const limitedRights = authScopes?.indexOf('user:limited') >= 0;
  const stripeButtonTitle = limitedRights
    ? intl.formatMessage({ id: 'StripePayoutPage.submitButtonText' })
    : null;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenStripePrompt = searchParams.get('stripeSetup') === 'true';
    const sessionListingId =
      typeof window !== 'undefined' && window.sessionStorage
        ? window.sessionStorage.getItem(STRIPE_SETUP_PROMPT_SESSION_KEY)
        : null;
    const shouldOpenFromSession = sessionListingId === listing?.id?.uuid;

    if (isOwnListing && showNoPayoutDetailsSet && (shouldOpenStripePrompt || shouldOpenFromSession)) {
      if (currentUser?.stripeAccount && !stripeAccountFetched) {
        onFetchStripeAccount();
      }
      const timerId = setTimeout(() => {
        setShowStripeSetupPrompt(true);
      }, 100);

      return () => clearTimeout(timerId);
    }
  }, [isOwnListing, showNoPayoutDetailsSet, location.search, listing?.id?.uuid]);

  const handleCloseStripeSetupPrompt = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('stripeSetup');
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(STRIPE_SETUP_PROMPT_SESSION_KEY);
    }
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : '',
    });
    setShowStripeSetupPrompt(false);
  };

  if (isOwnListing && showNoPayoutDetailsSet) {
    return (
      <>
        <Modal
          id="ListingPage.stripeSetupPrompt"
          isOpen={showStripeSetupPrompt}
          onClose={handleCloseStripeSetupPrompt}
          onManageDisableScrolling={onManageDisableScrolling}
          usePortal
        >
          <div>
            <Heading as="h2" rootClassName={css.modalTitle}>
              <FormattedMessage id="EditListingWizard.payoutModalTitleOneMoreThing" />
              <br />
              <FormattedMessage id="EditListingWizard.payoutModalTitlePayoutPreferences" />
            </Heading>
            <p className={css.modalMessage}>
              {stripeAccountData ? (
                <FormattedMessage
                  id="EditListingWizard.payoutModalInfo"
                  values={{ marketplaceName: config.marketplaceName }}
                />
              ) : (
                <FormattedMessage
                  id="EditListingWizard.payoutModalInfoNew"
                  values={{ marketplaceName: config.marketplaceName }}
                />
              )}
            </p>
            <StripeConnectAccountForm
              disabled={getAccountLinkInProgress}
              inProgress={payoutDetailsSaveInProgress}
              ready={payoutDetailsSaved}
              currentUser={currentUser}
              stripeBankAccountLastDigits={getBankAccountLast4Digits(stripeAccountData)}
              savedCountry={savedCountry}
              savedAccountType={savedAccountType}
              submitButtonText={intl.formatMessage({
                id: 'StripePayoutPage.submitButtonText',
              })}
              stripeAccountError={
                createStripeAccountError || updateStripeAccountError || fetchStripeAccountError
              }
              stripeAccountLinkError={getAccountLinkError}
              stripeAccountFetched={stripeAccountFetched}
              onChange={onPayoutDetailsChange}
              onSubmit={onPayoutDetailsSubmit}
              stripeConnected={stripeConnected}
              authScopes={authScopes}
            >
              {stripeConnected && requirementsMissing ? (
                <StripeConnectAccountStatusBox
                  type="verificationNeeded"
                  inProgress={getAccountLinkInProgress}
                  onGetStripeConnectAccountLink={handleGetStripeConnectAccountLink(
                    'custom_account_verification'
                  )}
                  disabled={limitedRights}
                  title={stripeButtonTitle}
                />
              ) : stripeConnected && savedCountry ? (
                <StripeConnectAccountStatusBox
                  type="verificationSuccess"
                  inProgress={getAccountLinkInProgress}
                  disabled={payoutDetailsSaveInProgress || limitedRights}
                  onGetStripeConnectAccountLink={handleGetStripeConnectAccountLink(
                    'custom_account_update'
                  )}
                  title={stripeButtonTitle}
                />
              ) : null}
            </StripeConnectAccountForm>
          </div>
        </Modal>
        <div className={classes}>
          <p className={classNames(css.ownListingText, css.missingPayoutDetailsText)}>
            <FormattedMessage id="ListingPage.addPayoutDetailsMessage" />
          </p>
          <NamedLink className={css.addPayoutDetails} name="StripePayoutPage">
            <EditIcon className={css.editIcon} />
            <FormattedMessage id="ListingPage.addPayoutDetails" />
          </NamedLink>
        </div>
      </>
    );
  } else if (isOwnListing) {
    let ownListingTextTranslationId = 'ListingPage.ownListing';

    if (isPendingApproval) {
      ownListingTextTranslationId = 'ListingPage.ownListingPendingApproval';
    } else if (isClosed) {
      ownListingTextTranslationId = 'ListingPage.ownClosedListing';
    } else if (isDraft) {
      ownListingTextTranslationId = 'ListingPage.ownListingDraft';
    }

    const message = isDraft ? 'ListingPage.finishListing' : 'ListingPage.editListing';

    const ownListingTextClasses = classNames(css.ownListingText, {
      [css.ownListingTextPendingApproval]: isPendingApproval,
    });

    const hasValidType = approvalToPublishOptions?.type && approvalToPublishOptions.type !== 'none';
    const isCTAEnabled = hasValidType && isPendingApproval;

    return (
      <>
        {modal ? (
          <ConfirmModal
            type={modal}
            onCancel={() => setModal(null)}
           onConfirm={() => {
              if (modal === 'hide') {
                onCloseListing(listing.id).then(() => history.push('/listings'));
              } else {
                onDeleteListing(listing.id).then(() => history.push('/listings'));
              }
              setModal(null);
            }}
          />
        ) : null}

        <div
          className={classNames(classes, {
            [css.actionBarWithCTAEnabled]: isCTAEnabled,
          })}
        >
          <p className={classNames(ownListingTextClasses, { [css.CTAEnabled]: isCTAEnabled })}>
            <FormattedMessage id={ownListingTextTranslationId} />
          </p>
          <span className={isCTAEnabled ? css.linkContainer : css.noShrink}>
            <span className={css.linkContainer}>
              <NamedLink
                className={classNames(css.editListingLink, css.promoteLink, {
                  [css.CTAEnabled]: isCTAEnabled,
                })}
                name="EditListingPage"
                params={{ ...editParams, tab: 'review' }}
              >
                <PromoteIcon className={css.promoteIcon} />
                <FormattedMessage id="ListingPage.promoteListing" />
              </NamedLink>
              <NamedLink
                className={classNames(css.editListingLink, { [css.CTAEnabled]: isCTAEnabled })}
                name="EditListingPage"
                params={editParams}
              >
                <EditIcon className={css.editIcon} />
                <FormattedMessage id={message} />
              </NamedLink>

              {(onCloseListing || onDeleteListing) && (
                <div className={css.removeListingWrapper}>
                  <button
                    className={css.removeListingButton}
                    onClick={e => {
                      e.preventDefault();
                      setDropdownOpen(o => !o);
                    }}
                  >
                    <svg
                      className={css.removeIcon}
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <FormattedMessage id="ListingPage.removeListing" />
                    <span className={css.dropdownArrow}>▾</span>
                  </button>
                  {dropdownOpen && (
                    <div className={css.removeDropdown}>
                      {!isClosed && onCloseListing && (
                        <button
                          className={css.removeDropdownItem}
                          onClick={e => {
                            e.preventDefault();
                            setDropdownOpen(false);
                            setModal('hide');
                          }}
                        >
                          <FormattedMessage id="ListingPage.hideListing" />
                        </button>
                      )}
                      {onDeleteListing && (
                        <button
                          className={css.removeDropdownItem}
                          onClick={e => {
                            e.preventDefault();
                            setDropdownOpen(false);
                            setModal('delete');
                          }}
                        >
                          <FormattedMessage id="ListingPage.deleteListing" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </span>
            <CTAButtonMaybe
              data={approvalToPublishOptions}
              routeConfiguration={routeConfiguration}
              listingId={listing?.id?.uuid}
              userId={currentUser?.id?.uuid}
              userEmail={currentUser?.attributes?.email}
              isPendingApproval={isPendingApproval}
            />
          </span>
        </div>
      </>
    );
  } else if (isClosed) {
    return (
      <div className={classes}>
        <p className={css.closedListingText}>
          <FormattedMessage id="ListingPage.closedListing" />
        </p>
      </div>
    );
  }
  return null;
};

export default ActionBarMaybe;
